// =============================================================================
// announcements-admin-client.http.ts — Adaptateur HTTP RÉEL du AnnouncementsAdminClient (R15-COM-03).
// =============================================================================
// Remplace le stub-first derrière le MÊME DI seam (AnnouncementsAdminClientProvider) : les hooks/écrans
// consomment l'interface, l'impl est substituée sans toucher l'UI. AUTORITÉ = API (NestJS, AdminSessionGuard,
// écriture sous withAdmin, RLS). Cet adaptateur ne porte AUCUNE règle métier : il sérialise, parse, mappe.
//
// SÉCURITÉ (RULE_REF_006) : la session admin est portée par le COOKIE httpOnly posé par l'API
// (`credentials: 'include'`) — l'UI ne lit ni n'écrit le secret de session. LECTURES (GET) : aucun en-tête
// CSRF. MUTATIONS (POST/PATCH) : en-tête double-submit `x-csrf-token` = cookie NON-httpOnly `admin_csrf`
// (parité HttpLegalAdminClient / catalog-edition). Sans l'en-tête, toute écriture est 403 (fail-closed serveur).

import {
  AnnouncementsAdminApiError,
  type AnnouncementContentPatch,
  type AnnouncementDto,
  type AnnouncementListFilters,
  type AnnouncementsAdminClient,
  type CreateAnnouncementInput,
  type EligibleCampaignDto,
} from '@/api/announcements-admin-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine). `undefined`
 * si absent (pas de session, ou contexte sans document) — l'appel mutant partira alors sans en-tête et
 * l'API répondra 403 (fail-closed serveur). Parité stricte legal-admin / catalog-edition.
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/** Construit la query string des filtres du registre (n'émet que les filtres présents). */
function buildListQuery(filters: AnnouncementListFilters | undefined): string {
  if (filters === undefined) {
    return '';
  }
  const params = new URLSearchParams();
  if (filters.state !== undefined) {
    params.set('state', filters.state);
  }
  if (filters.campaignId !== undefined) {
    params.set('campaignId', filters.campaignId);
  }
  if (filters.from !== undefined) {
    params.set('from', filters.from);
  }
  if (filters.to !== undefined) {
    params.set('to', filters.to);
  }
  const query = params.toString();
  return query.length > 0 ? `?${query}` : '';
}

/**
 * Adaptateur HTTP réel du client ANNONCES ADMIN. `baseUrl` par défaut `/api` (SPA et API partagent
 * l'origine, ou proxy). En test, fetch est mocké.
 */
export class HttpAnnouncementsAdminClient implements AnnouncementsAdminClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async list(
    filters?: AnnouncementListFilters,
  ): Promise<readonly AnnouncementDto[]> {
    return this.get<readonly AnnouncementDto[]>(
      `/admin/announcements${buildListQuery(filters)}`,
    );
  }

  public async eligibleCampaigns(): Promise<readonly EligibleCampaignDto[]> {
    return this.get<readonly EligibleCampaignDto[]>(
      '/admin/announcements/eligible-campaigns',
    );
  }

  public async create(input: CreateAnnouncementInput): Promise<AnnouncementDto> {
    return this.mutate<AnnouncementDto>('POST', '/admin/announcements', {
      campaignId: input.campaignId,
      titre: input.titre,
      contentRich: input.contentRich,
    });
  }

  public async updateDraft(
    announcementId: string,
    patch: AnnouncementContentPatch,
  ): Promise<AnnouncementDto> {
    return this.mutate<AnnouncementDto>(
      'PATCH',
      `/admin/announcements/${encodeURIComponent(announcementId)}`,
      this.patchBody(patch),
    );
  }

  public async schedule(
    announcementId: string,
    scheduledFor: string,
  ): Promise<AnnouncementDto> {
    return this.mutate<AnnouncementDto>(
      'POST',
      `/admin/announcements/${encodeURIComponent(announcementId)}/schedule`,
      { scheduledFor },
    );
  }

  public async publishNow(announcementId: string): Promise<AnnouncementDto> {
    return this.mutate<AnnouncementDto>(
      'POST',
      `/admin/announcements/${encodeURIComponent(announcementId)}/publish-now`,
    );
  }

  public async editContent(
    announcementId: string,
    patch: AnnouncementContentPatch,
  ): Promise<AnnouncementDto> {
    return this.mutate<AnnouncementDto>(
      'PATCH',
      `/admin/announcements/${encodeURIComponent(announcementId)}/content`,
      this.patchBody(patch),
    );
  }

  public async cancel(announcementId: string): Promise<AnnouncementDto> {
    return this.mutate<AnnouncementDto>(
      'POST',
      `/admin/announcements/${encodeURIComponent(announcementId)}/cancel`,
    );
  }

  /** N'inclut que les champs présents du patch (un champ absent = inchangé côté serveur). */
  private patchBody(patch: AnnouncementContentPatch): Record<string, unknown> {
    return {
      ...(patch.titre !== undefined ? { titre: patch.titre } : {}),
      ...(patch.contentRich !== undefined
        ? { contentRich: patch.contentRich }
        : {}),
    };
  }

  /** GET same-origin (cookie httpOnly), parse le JSON typé, ou lève une AnnouncementsAdminApiError. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /**
   * Mutation same-origin : pose l'en-tête double-submit `x-csrf-token` = cookie `admin_csrf`. Parse le
   * JSON typé (chaque commande renvoie l'annonce résultante). Erreur HTTP → AnnouncementsAdminApiError.
   */
  private async mutate<T>(
    method: 'POST' | 'PATCH',
    path: string,
    body?: Record<string, unknown>,
  ): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const csrf = readCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse d'erreur en AnnouncementsAdminApiError (message serveur, sinon repli). */
  private async toApiError(
    response: Response,
  ): Promise<AnnouncementsAdminApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur les annonces admin.`;
    return new AnnouncementsAdminApiError(message, response.status);
  }
}
